// Канонические иконки-эмблемы разделов — ЕДИНЫЙ источник истины.
// Один тип контента = одна иконка во всей системе (сайдбар, витрина тулзов
// на дашборде, шапки страниц, empty-state). Пользователь запоминает иконку и
// ассоциирует её с разделом — поэтому она обязана совпадать везде.
//
// Если иконка раздела меняется — правим ТОЛЬКО здесь, и она разъезжается по
// всем поверхностям, импортирующим этот реестр. Не хардкодить Icon* в шапках.
import type { Category } from "@studyhub/ui/components/studyhub/category";
import type { TintColor } from "@studyhub/ui/components/studyhub/tint-tile";
import {
	type Icon,
	IconBook,
	IconCalendarClock,
	IconCalendarMonth,
	IconCards,
	IconChartLine,
	IconChecklist,
	IconCircleDot,
	IconFolder,
	IconFolders,
	IconHeadphones,
	IconHome,
	IconLibrary,
	IconMasksTheater,
	IconMessage2,
	IconMicrophone,
	IconNotebook,
	IconPencil,
	IconPhotoScan,
	IconRoute,
	IconStar,
	IconTarget,
	IconTrash,
	IconUpload,
	IconWaveSine,
} from "@tabler/icons-react";

/**
 * Ключ раздела → каноническая Tabler-иконка. Значения сознательно
 * согласованы с решениями по консистентности:
 * - materials → IconLibrary (стопка, а НЕ IconCopy — он читался как «копировать»)
 * - visuals → IconPhotoScan (рамка-сканер со страницы, а не IconFrame)
 * - voiceTutor → IconWaveSine (волна, а не микрофон — микрофон у «Запись лекции»)
 * - essay → IconPencil (карандаш-правка, а не IconFileTextAi)
 * - glossary → IconBook (а не IconVocabulary)
 * - tutor → IconMessage2 (а не IconMessages/IconBrandWechat)
 * - weakSpots → IconTarget (мишень-пробел, а не IconAlertTriangle)
 * - studyPlan → IconCalendarClock (часы = подготовка к сроку/дедлайну)
 * - studyCalendar → IconCalendarMonth (месячная сетка — отличается от часов
 *   у «Учебного плана», иначе разделы визуально путаются)
 * - course → IconRoute (маршрут обучения; курс — вершина иерархии
 *   Библиотека → Курс → Тема, см. docs/plans/LEARNING-SYSTEM-UI.md §1)
 * - topic → IconCircleDot (точка маршрута ●──○──·)
 * - folder → IconFolder (одна папка; IconFolders остался за легаси-«Коллекциями»)
 * - library → IconLibrary (тот же знак, что и у materials: «Библиотека» —
 *   это и есть переименованные материалы, знак не должен меняться)
 */
export const SECTION_ICONS = {
	home: IconHome,
	studyPlan: IconCalendarClock,
	studyCalendar: IconCalendarMonth,
	course: IconRoute,
	topic: IconCircleDot,
	folder: IconFolder,
	library: IconLibrary,
	collections: IconFolders,
	materials: IconLibrary,
	favorites: IconStar,
	tutor: IconMessage2,
	notes: IconNotebook,
	tests: IconChecklist,
	flashcards: IconCards,
	glossary: IconBook,
	recordings: IconMicrophone,
	voiceTutor: IconWaveSine,
	audioRecap: IconHeadphones,
	essay: IconPencil,
	visuals: IconPhotoScan,
	progress: IconChartLine,
	weakSpots: IconTarget,
	trash: IconTrash,
	upload: IconUpload,
	// Внешний модуль `characters` (Ш7): маска, а не «человечек» — персонаж,
	// а не пользователь. Иконку пользователя знак раздела занять не может,
	// она занята профилем.
	characters: IconMasksTheater,
} as const satisfies Record<string, Icon>;

export type SectionIconKey = keyof typeof SECTION_ICONS;

/**
 * Канонический ЦВЕТ-эмблема инструмента (tint плитки/иконки-шапки). Тот же
 * tint используется и в витрине тулзов (FeatureLauncher), и в шапке страницы
 * (SectionHeader `tint=`), чтобы цвет ассоциировался с разделом везде.
 * Уникальные различимые hue (8 инструментов): чат-янтарь матчится с AI-тьютором.
 * Палитра расширена токенами rose/emerald/yellow/blue (tokens.css + styles.css).
 */
export const SECTION_TINT = {
	tutor: "amber",
	recordings: "rose",
	voiceTutor: "indigo",
	audioRecap: "info",
	essay: "success",
	visuals: "emerald",
	glossary: "yellow",
	upload: "blue",
	characters: "violet",
	tests: "pink",
	flashcards: "teal",
} as const satisfies Partial<Record<SectionIconKey, TintColor>>;

/**
 * Канонический ЦВЕТ-эмблема сущности в терминах «Картотеки» (`Category` →
 * пары `--c-*-bg`/`--c-*-fg`). Используется `CategoryTile` / `TactileToken` /
 * `TiltCard` — там, где нужен not-tint, а именно категорийная плитка.
 *
 * Ролевая палитра обучающей системы — `docs/plans/LEARNING-SYSTEM-UI.md` §1:
 * курс — единственная новая сущность с «полным» акцентом (`ai`), тема
 * наследует его приглушённо, папка сознательно нейтральна (`time`), потому
 * что она контейнер, а не смысл.
 *
 * ВАЖНО: это ОТДЕЛЬНАЯ карта от `SECTION_TINT` — там тип `TintColor`
 * (tint-плитки), здесь `Category` (cat-* классы). Не смешивать.
 */
export const SECTION_CATEGORY = {
	course: "ai",
	topic: "ai",
	folder: "time",
	library: "doc",
	materials: "doc",
	notes: "doc",
	audioRecap: "image",
	flashcards: "cards",
	tests: "quiz",
	tutor: "ai",
	glossary: "text",
	// Пробелы приходят из тестов — и знак у них тот же, что у тестов. Шапка
	// `/weak-spots` рисовала `category="quiz"` строкой в разметке; запись здесь
	// делает этот факт общим, чтобы баннер раздела не разошёлся с шапкой в цвете.
	weakSpots: "quiz",
} as const satisfies Partial<Record<SectionIconKey, Category>>;
